import { useState, useEffect } from 'react'
import { NavBar, Form, Field, Radio, Button, Toast, Card, Tabs, Tag, Dialog, Empty, Loading } from 'react-vant'
import { ArrowLeft, BullhornO, Success, ShieldO, DeleteO, ClockO } from '@react-vant/icons'
import { useNavigate } from 'react-router-dom'
import request from '../../api/request'
import './NotificationRelease.css'

export default function AdminNotificationRelease() {
    const navigate = useNavigate()
    const [form] = Form.useForm()
    const [loading, setLoading] = useState(false)
    const [activeTab, setActiveTab] = useState('create')

    // 记录列表相关
    const [history, setHistory] = useState([])
    const [fetching, setFetching] = useState(false)

    const fetchHistory = async () => {
        setFetching(true)
        try {
            const res = await request.get('/notifications/', { params: { page_size: 50 } })
            setHistory(res.items || [])
        } catch (error) {
            console.error('Failed to fetch history:', error)
        } finally {
            setFetching(false)
        }
    }

    useEffect(() => {
        if (activeTab === 'history') {
            fetchHistory()
        }
    }, [activeTab])

    const onFinish = async (values) => {
        setLoading(true)
        try {
            const payload = {
                title: values.title,
                content: values.content,
                type: values.type,
                target_role: values.target === 'all' ? null : values.target
            }

            await request.post('/notifications/', payload)
            Toast.success('发布成功')
            form.resetFields()
            setActiveTab('history') // 发布后跳转到历史记录
        } catch (error) {
            console.error('Failed to post notification:', error)
            Toast.fail('发布失败: ' + (error.response?.data?.detail || '网络异常'))
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = (id) => {
        Dialog.confirm({
            title: '确认撤回',
            message: '撤回后该通知将从所有用户的公告栏中消失，且不可恢复。确认撤回吗？',
            onConfirm: async () => {
                try {
                    await request.delete(`/notifications/${id}/`)
                    Toast.success('已成功撤回')
                    fetchHistory()
                } catch (error) {
                    Toast.fail('操作失败')
                }
            }
        })
    }

    const getTargetTag = (role) => {
        const map = {
            null: { label: '全员', color: '#7c3aed' },
            'teacher': { label: '仅教师', color: '#00b578' },
            'parent': { label: '仅家长', color: '#00b578' }
        }
        const config = map[role] || map[null]
        return <Tag style={{ background: config.color + '20', color: config.color, border: 'none' }}>{config.label}</Tag>
    }

    return (
        <div className="admin-page-premium">
            <NavBar
                title="通知分发中心"
                leftArrow={<ArrowLeft />}
                onClickLeft={() => navigate(-1)}
                fixed
                placeholder
            />

            <div className="premium-segmented-container">
                <div className="premium-segmented-control">
                    <div
                        className={`segmented-item ${activeTab === 'create' ? 'active' : ''}`}
                        onClick={() => setActiveTab('create')}
                    >
                        发布新通知
                    </div>
                    <div
                        className={`segmented-item ${activeTab === 'history' ? 'active' : ''}`}
                        onClick={() => setActiveTab('history')}
                    >
                        发送记录
                    </div>
                    {/* 滑块背景 */}
                    <div
                        className="segmented-slider"
                        style={{
                            transform: `translateX(${activeTab === 'history' ? '100%' : '0'})`,
                        }}
                    />
                </div>
            </div>

            <div style={{ padding: '4px 16px 20px' }}>
                {/* 发布表单 - 保持挂载以防止数据丢失 */}
                <div style={{ display: activeTab === 'create' ? 'block' : 'none' }}>
                    <Card className="premium-card" style={{ marginTop: 16 }}>
                        <Card.Body>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
                                <div className="premium-icon-wrapper">
                                    <BullhornO color="#7c3aed" size={24} />
                                </div>
                                <div>
                                    <div style={{ fontSize: 17, fontWeight: '700', color: '#0f172a', marginBottom: 2 }}>撰写新通知</div>
                                    <div style={{ fontSize: 12, color: '#94a3b8' }}>系统将通过端内推送及公告栏即时送达</div>
                                </div>
                            </div>

                            <Form
                                form={form}
                                onFinish={onFinish}
                                layout="vertical"
                                initialValues={{
                                    type: 'system',
                                    target: 'all'
                                }}
                                footer={
                                    <div style={{ marginTop: 32 }}>
                                        <Button
                                            round
                                            block
                                            type='primary'
                                            nativeType='submit'
                                            loading={loading}
                                            icon={<Success />}
                                            className="premium-publish-btn"
                                        >
                                            立即发布
                                        </Button>
                                        <div className="security-tag">
                                            <ShieldO size={11} /> 端到端安全传输加密中
                                        </div>
                                    </div>
                                }
                            >
                                <Form.Item
                                    name='title'
                                    label='通知标题'
                                    rules={[{ required: true, message: '请填写标题' }]}
                                >
                                    <Field placeholder='请输入简明扼要的标题' className="premium-field" />
                                </Form.Item>

                                <Form.Item
                                    name='content'
                                    label='通知内容'
                                    rules={[{ required: true, message: '请填写内容' }]}
                                >
                                    <Field
                                        rows={5}
                                        type='textarea'
                                        placeholder='请输入通知的详细内容，支持多行输入...'
                                        className="premium-field"
                                    />
                                </Form.Item>

                                <Form.Item
                                    name='type'
                                    label='分类标签'
                                >
                                    <Radio.Group direction='horizontal' className="premium-radio-group">
                                        <Radio name='system'>系统公告</Radio>
                                        <Radio name='academic'>教务通知</Radio>
                                        <Radio name='sports'>体育赛事</Radio>
                                    </Radio.Group>
                                </Form.Item>

                                <Form.Item
                                    name='target'
                                    label='分发范围'
                                >
                                    <Radio.Group direction='horizontal' className="premium-radio-group">
                                        <Radio name='all'>全员可见</Radio>
                                        <Radio name='teacher'>仅教师</Radio>
                                        <Radio name='parent'>仅家长</Radio>
                                    </Radio.Group>
                                </Form.Item>
                            </Form>
                        </Card.Body>
                    </Card>
                </div>

                {/* 历史记录 - 动态加载 */}
                <div style={{ display: activeTab === 'history' ? 'block' : 'none', marginTop: 16, minHeight: '400px' }}>
                    {fetching ? (
                        <div style={{ textAlign: 'center', padding: '100px 0' }}>
                            <Loading vertical>正在全力加载记录...</Loading>
                        </div>
                    ) : (history && history.length > 0) ? (
                        history.map(item => (
                            <Card key={item.id} className="premium-history-card">
                                <Card.Body>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                                        <div style={{ flex: 1, paddingRight: 12 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                                {getTargetTag(item.target_role)}
                                                <span style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{item.title}</span>
                                            </div>
                                            <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.5 }}>{item.content}</div>
                                        </div>
                                        <Button
                                            size="small"
                                            icon={<DeleteO />}
                                            onClick={() => handleDelete(item.id)}
                                            className="history-delete-btn"
                                        />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', fontSize: 11, color: '#94a3b8' }}>
                                        <ClockO style={{ marginRight: 4 }} />
                                        发布于：{new Date(item.created_at).toLocaleString()}
                                    </div>
                                </Card.Body>
                            </Card>
                        ))
                    ) : (
                        <div style={{ paddingTop: '60px' }}>
                            <Empty description="暂无发送记录" />
                        </div>
                    )}
                </div>

                {/* 发布提示 */}
                <div className="premium-info-box" style={{ marginTop: 24 }}>
                    <div className="premium-info-title">
                        <BullhornO size={16} /> 发布须知
                    </div>
                    <div className="premium-info-content">
                        • 发布后的通知可通过“发送记录”页签进行撤回。<br />
                        • 撤回操作将即时从接收终端同步删除相关内容。<br />
                        • 紧急通知将通过系统级 App Push 同步推送。
                    </div>
                </div>
            </div>
        </div>
    )
}
